import { create } from 'zustand';

export const PresenceStatuses = {
  ONLINE: 'ONLINE',
  AWAY: 'AWAY',
  OFFLINE: 'OFFLINE',
} as const;

export type PresenceStatus = (typeof PresenceStatuses)[keyof typeof PresenceStatuses];

type PresenceSlice = {
  /** Статусы тех, про кого сервер уже ответил. Пока ответа нет — индикатор не рисуем. */
  statusByUserId: Record<string, PresenceStatus>;
  /** Сколько компонентов сейчас смотрят на этого человека. Ноль — перестаём про него спрашивать. */
  watchersByUserId: Record<string, number>;
  watch: (userId: string) => void;
  unwatch: (userId: string) => void;
  applyStatuses: (statuses: Array<{ userId: string; status: PresenceStatus }>) => void;
};

export const usePresenceStore = create<PresenceSlice>((set) => ({
  statusByUserId: {},
  watchersByUserId: {},

  watch: (userId) =>
    set((state) => ({
      watchersByUserId: {
        ...state.watchersByUserId,
        [userId]: (state.watchersByUserId[userId] ?? 0) + 1,
      },
    })),

  unwatch: (userId) =>
    set((state) => {
      const next = (state.watchersByUserId[userId] ?? 0) - 1;
      const watchersByUserId = { ...state.watchersByUserId };
      const statusByUserId = { ...state.statusByUserId };

      if (next > 0) {
        watchersByUserId[userId] = next;
        return { watchersByUserId };
      }

      // На него больше никто не смотрит: убираем и из опроса, и из кэша — иначе, вернувшись
      // на экран через час, человек увидит зелёную точку годичной давности до первого ответа.
      delete watchersByUserId[userId];
      delete statusByUserId[userId];
      return { watchersByUserId, statusByUserId };
    }),

  applyStatuses: (statuses) =>
    set((state) => {
      const statusByUserId = { ...state.statusByUserId };
      for (const entry of statuses) {
        statusByUserId[entry.userId] = entry.status;
      }
      return { statusByUserId };
    }),
}));

export const selectPresenceStatus = (userId?: string | null) => (state: PresenceSlice) =>
  userId ? state.statusByUserId[userId] : undefined;

export const selectWatchedUserIds = (state: PresenceSlice) => state.watchersByUserId;
